import { TaskPipeline } from '@/components/TaskPipeline'
import { Picker } from '@/components/Picker'
/** Shared briefs and rubrics, using a deliberately limited read-only API. */
import { useCallback, useMemo } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { api } from '@/api/client'
import { useAsync } from '@/components/useAsync'
import { Icon } from '@/components/Icon'
import { Avatar } from '@/components/Avatar'
import { BriefView } from '@/components/BriefForm'
import { RubricView } from '@/components/RubricView'
import { Section } from '@/components/Section'
import { SCIENCE_PROGRAM, programLabel, taskPrograms, isScienceExpert } from '@/lib/programs'
import { useProfile } from '@/auth/ProfileContext'
import type { Task } from '@/lib/types'
import { WorkTree, type TreeTask } from '@/components/WorkTree'
import { useStickySearch } from '@/lib/stickySearch'
import { intakeStage, pipelineFilterKey, workflowStage } from '@/lib/stages'
import { INTAKE_API, type IntakeSummary } from '@/lib/templateIntakes'

type SharedTask = Pick<Task, 'task_id' | 'title' | 'program' | 'programs' | 'display_stage' | 'stage' | 'stage_info' | 'kind' | 'workflow_version' | 'sme_id' | 'updated_at' | 'collection' | 'source_template' | 'scientific_template_id'> & { author_name: string; authoring?: { status?: string } }
type SharedContent = SharedTask & Pick<Task, 'brief' | 'prompt' | 'rubric' | 'rubric_layout'>

export function TaskDirectory() {
  const { profile } = useProfile()
  const science = isScienceExpert(profile)
  const noun = science ? 'workflow' : 'task'
  const tasks = useAsync(() => api.get<{ tasks: SharedTask[]; physics_only?: boolean }>('/sme/task-directory'))
  const intakes = useAsync(async () => {
    if (!science) return { enabled: false, templates: [] as IntakeSummary[] }
    const meta = await api.get<{ science_template_intake_enabled?: boolean }>('/meta')
    if (!meta.science_template_intake_enabled) return { enabled: false, templates: [] as IntakeSummary[] }
    const result = await api.get<{ templates: IntakeSummary[] }>(INTAKE_API, { shared: '1' })
    return { enabled: true, templates: result.templates }
  }, [science])
  const withTemplates = science && !!intakes.data?.enabled
  const [params, setParams] = useStickySearch('task-directory', ['q', 'program', 'stage'])
  const query = params.get('q') ?? ''
  const physicsOnly = !!tasks.data?.physics_only
  const program = physicsOnly ? '' : params.get('program') ?? ''
  const rawStage = params.get('stage') ?? ''
  const templates = useMemo(() => withTemplates ? (intakes.data?.templates ?? []).map(template => ({ ...template,
    owner_name: template.owner_name ?? (template.owner_id === profile?.user_id ? profile.name : template.owner_email),
  })) : [], [withTemplates, intakes.data, profile])
  const programs = [...new Set([...(tasks.data?.tasks ?? []).flatMap(taskPrograms), ...(templates.length ? [SCIENCE_PROGRAM] : [])])].sort()
  const scientific = science && !!tasks.data?.tasks.length && tasks.data.tasks.every(t => t.kind === 'scientific_workflow' || t.collection)
  // Only the six-stage board uses its own keys; the task flow still filters by stored stage.
  const stage = scientific ? pipelineFilterKey(rawStage) : rawStage
  function filter(key: string, value: string) {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value); else next.delete(key)
    setParams(next, { replace: true })
  }
  const text = useCallback((value: string) => value.toLowerCase().includes(query.toLowerCase()), [query])
  const matchTask = useCallback((task: TreeTask) => (!program || taskPrograms(task as Task).includes(program))
    && (!stage || (scientific ? workflowStage(task) : String(task.display_stage ?? task.stage)) === stage)
    && text(`${task.title} ${task.author_name} ${programLabel(task as Task)}`), [program, stage, scientific, text])
  const matchTemplate = useCallback((template: IntakeSummary) => (!program || program === SCIENCE_PROGRAM)
    && (!stage || intakeStage(template.status) === stage)
    && text(`${template.title} ${template.owner_name ?? template.owner_email} ${SCIENCE_PROGRAM}`), [program, stage, text])
  // Physics workflows open their review page; other tasks open read-only.
  const taskHref = useCallback((task: TreeTask) => task.collection ? `/sme/tasks/${task.task_id}` : `/sme/task-directory/${task.task_id}`, [])
  const templateHref = useCallback((template: IntakeSummary) => `/sme/templates/${template.template_id}`, [])
  const memberHref = useCallback((task: TreeTask, member: { task_id: string }) => `/sme/tasks/${task.task_id}?task=${member.task_id}&section=prompt`, [])
  return <>
    <div className="page-head"><div><h1>{withTemplates ? 'All data' : `All ${noun}s`}</h1><p className="muted">{withTemplates ? 'Browse templates, the workflows built from them and their tasks.' : physicsOnly ? 'Browse physics workflows and their tasks.' : 'Explore briefs and rubrics from other experts.'}</p></div></div>
    {tasks.data && <TaskPipeline scientific={scientific} tasks={tasks.data.tasks} intakes={withTemplates ? templates : undefined} selected={stage} onSelect={value => filter('stage', value)} />}
    {stage && <button className="link" onClick={() => filter('stage', '')}>Show all stages</button>}
    <div className="task-directory-toolbar">
      {!physicsOnly && <Picker label="Program" value={program} onChange={value => filter('program', value)}
        options={[{ value: '', label: 'All programs' }, ...programs.map(value => ({ value, label: value }))]} />}
      <label className="list-search"><Icon name="search" size={17} /><input type="search" aria-label={withTemplates ? 'Search workflows, templates or experts' : `Search ${noun}s or experts`} placeholder={withTemplates ? 'Search data or experts…' : `Search ${noun}s or experts…`} value={query} onChange={event => filter('q', event.target.value)} /></label>
    </div>
    {tasks.error && <div className="note err" role="alert">{tasks.error}</div>}
    {intakes.error && <div className="note err" role="alert">{intakes.error}</div>}
    <WorkTree tasks={tasks.data?.tasks ?? []} templates={templates} loading={tasks.loading || intakes.loading}
      empty={`No ${withTemplates ? 'data' : `${noun}s`} match.`} matchTask={matchTask} matchTemplate={matchTemplate}
      taskHref={taskHref} templateHref={templateHref} memberHref={memberHref}
      expert={({ task, intake }) => {
        const name = task ? task.author_name : intake?.owner_name ?? intake?.owner_email
        return name ? <span className="expert-cell"><Avatar name={name} size={26} /><span>{name}</span></span> : null
      }} />
  </>
}

export function DirectoryTask() {
  const science = isScienceExpert(useProfile().profile)
  const { taskId } = useParams()
  const detail = useAsync(() => api.get<{ task: SharedContent }>(`/sme/task-directory/${taskId}`), [taskId])
  const [params] = useSearchParams()
  const section = params.get('section') === 'rubric' ? 'rubric' : 'brief'
  const task = detail.data?.task
  return <>
    <Link to="/sme/task-directory" className="back">← All {science ? 'workflows' : 'tasks'}</Link>
    {detail.error && <div className="note err" role="alert">{detail.error}</div>}
    {detail.loading ? <p className="muted">Loading…</p> : task && <>
      <div className="page-head"><div><h1>{task.title}</h1><p className="muted">{task.author_name} · {programLabel(task)}</p></div></div>
      <nav className="task-tabs" aria-label={science ? 'Workflow content' : 'Task content'}>
        {(['brief', 'rubric'] as const).map(value => <Link key={value} to={value === 'brief' ? '?' : '?section=rubric'} className={`task-tab${section === value ? ' active' : ''}`}
          aria-current={section === value ? 'page' : undefined} replace>
          {value === 'brief' ? 'Brief' : 'Rubric'}
        </Link>)}
      </nav>
      {section === 'brief' ? <>
        <Section title="Agent prompt"><p className="prewrap">{task.prompt}</p></Section>
        <BriefView brief={task.brief} />
      </> : <RubricView rubric={task.rubric} layout={task.rubric_layout} />}
    </>}
  </>
}
